<script setup lang="ts">
/**
 * Посадочная страница под поисковый кластер: презентации, КП, лендинги.
 *
 * Одна разметка на три страницы, но НЕ один текст: содержание целиком приходит
 * из `src/site/clusters.ts`, и там оно у каждой своё — свои шаги, свой разбор
 * возражений, свои вопросы, свои шаблоны. Переставленные слова в одном тексте
 * — это дорвей, их Яндекс снимает пачками; да и по делу сборка презентации, КП
 * и лендинга различается и материалами на входе, и результатом.
 *
 * Оформление — то же самое, что на витрине: свой визуальный язык для четырёх
 * страниц одного сайта означал бы, что переход между ними читается как переход
 * на чужой сайт.
 */
import { computed } from 'vue';
import SiteFooter from '../../components/marketing/SiteFooter.vue';
import SiteHeader from '../../components/marketing/SiteHeader.vue';
import { CLUSTERS, type Cluster } from '../../site/clusters.js';
import { pricing } from '../../site/public-data.js';
import TaskField from './TaskField.vue';
import { formatPrice } from '@zetter/shared';
import './landing.css';

const props = defineProps<{ cluster: Cluster }>();

/**
 * Навигация ведёт на соседние кластеры, а не на якоря витрины.
 *
 * Это и удобство, и связность: страницы одного сайта, ссылающиеся друг на
 * друга, поиск разбирает как раздел, а не как три случайных документа.
 */
const nav = computed(() => [
  ...CLUSTERS.filter((item) => item.slug !== props.cluster.slug).map((item) => ({
    href: `/${item.slug}`,
    label: item.navLabel,
  })),
  { href: '/#pricing', label: 'Цены' },
]);

/** Цена документа: та же, что в блоке цен на витрине. */
const docPrice = computed(() =>
  pricing.value ? formatPrice(pricing.value.creditKopecks) : null,
);
</script>

<template>
  <div class="lp">
    <SiteHeader :nav="nav" />

    <!-- Штамп «эта страница собрана в Keypi» — тот же приём, что на витрине. -->
    <div class="lp-meta">
      <div class="lp-wrap lp-meta__inner">
        <span>{{ cluster.stamp }} · ИСПОЛНИТЕЛЬ: АГЕНТ KEYPI</span>
        <span>ВЕРСИЯ 12 · АКТУАЛЬНАЯ · СОБРАНО ЗА 17 МИНУТ</span>
      </div>
    </div>

    <!-- ─── первый экран ──────────────────────────────────────────────── -->
    <section class="lp-wrap lp-section lp-cluster-hero">
      <div class="lp-eyebrow">{{ cluster.eyebrow }}</div>
      <h1 class="lp-h1 lp-h1--cluster">{{ cluster.h1 }}</h1>
      <p class="lp-lede">{{ cluster.lede }}</p>

      <ul class="lp-bullets">
        <li v-for="line in cluster.bullets" :key="line">{{ line }}</li>
      </ul>

      <TaskField :kind="cluster.kind" />

      <p class="lp-note">{{ cluster.note }}</p>
    </section>

    <!-- ─── как это работает ──────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">01</span>
        <h2 class="lp-h2">{{ cluster.stepsTitle }}</h2>
      </div>
      <div class="lp-steps">
        <div v-for="step in cluster.steps" :key="step.num" class="lp-step">
          <span class="lp-step__num">{{ step.num }}</span>
          <h3 class="lp-h3">{{ step.title }}</h3>
          <p class="lp-p">{{ step.text }}</p>
        </div>
      </div>
    </section>

    <!-- ─── чем отличается ────────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">02</span>
        <h2 class="lp-h2">{{ cluster.gainsTitle }}</h2>
      </div>
      <div class="lp-grid lp-grid--2">
        <article v-for="gain in cluster.gains" :key="gain.title" class="lp-card lp-card--tight">
          <h3 class="lp-h4">{{ gain.title }}</h3>
          <p class="lp-p lp-p--muted">{{ gain.text }}</p>
        </article>
      </div>
    </section>

    <!-- ─── шаблоны ───────────────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">03</span>
        <h2 class="lp-h2">{{ cluster.templatesTitle }}</h2>
      </div>
      <p class="lp-sub">{{ cluster.templatesNote }}</p>
      <div class="lp-tmpl">
        <span v-for="name in cluster.templates" :key="name" class="lp-tmpl__item">{{ name }}</span>
      </div>
    </section>

    <!-- ─── цены ──────────────────────────────────────────────────────── -->
    <!--
      Линейка планов не повторяется: она живёт на витрине, а две копии одной
      таблицы цен — это и расхождение при первой же правке тарифа, и две
      страницы, конкурирующие в поиске за один запрос.
    -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">04</span>
        <h2 class="lp-h2">Сколько это стоит</h2>
      </div>
      <p class="lp-sub">
        <template v-if="docPrice">
          Единица счёта — готовый документ, а не токены и не правки: {{ docPrice }} за документ.
          Списывается на первой сборке, правки к готовому документу бесплатны. Подписка включает
          пакет документов на месяц.
        </template>
        <template v-else>
          Единица счёта — готовый документ, а не токены и не правки. Списывается на первой
          сборке, правки к готовому документу бесплатны. Подписка включает пакет документов
          на месяц.
        </template>
      </p>
      <div class="lp-cluster-cta">
        <a href="/#pricing" class="lp-btn lp-btn--primary">Посмотреть тарифы</a>
      </div>
    </section>

    <!-- ─── частые вопросы ────────────────────────────────────────────── -->
    <section class="lp-wrap lp-section">
      <div class="lp-head">
        <span class="lp-num">05</span>
        <h2 class="lp-h2">Частые вопросы</h2>
      </div>
      <div class="lp-faq">
        <div v-for="item in cluster.faq" :key="item.q" class="lp-faq__item">
          <h3 class="lp-faq__q">{{ item.q }}</h3>
          <p class="lp-faq__a">{{ item.a }}</p>
        </div>
      </div>
    </section>

    <!-- ─── призыв ────────────────────────────────────────────────────── -->
    <section class="lp-dark lp-cta">
      <div class="lp-wrap lp-cta__inner">
        <div>
          <div class="lp-cta__title">{{ cluster.ctaTitle }}</div>
          <div class="lp-cta__note">{{ cluster.note }}</div>
        </div>
        <a href="/register" class="lp-btn lp-btn--light lp-btn--lg">Начать бесплатно</a>
      </div>
    </section>

    <!--
      Ссылка на витрину обязательна: посадочная страница рассказывает про один
      тип документа, а продукт собирает двенадцать, и человеку, пришедшему за
      презентацией, нужно куда-то узнать остальное.
    -->
    <div class="lp-meta lp-meta--foot">
      <div class="lp-wrap lp-meta__center">
        <a href="/" class="lp-footer__link">KEYPI СОБИРАЕТ ЕЩЁ КП, ПРАЙСЫ, КЕЙСЫ, МЕНЮ И АФИШИ →</a>
      </div>
    </div>

    <SiteFooter />
  </div>
</template>
